import h from 'hyperscript';

import { isOnBg } from '@disy/lib/colors';
import '@disy/ui/alert.css';
import { escapeAndMarkHTML } from '@disy/ui/escape-and-mark';
import '@disy/ui/truncated-p';
// The following import is used only for type checking and then removed.
import type { TruncatedP } from '@disy/ui/truncated-p';

// we need the array of strings for internal implementation
const ALERT_TYPES = ['error', 'info', 'success', 'warning', 'hint'] as const;
const COMPONENT_NAME = 'd-alert';

// derive the type from the array of strings
export type AlertType = (typeof ALERT_TYPES)[number];

/**
 * The alert lit component has the following attributes
 *
 * @attr message
 * @attr title
 * @attr truncated-message
 * @attr type
 */
export class Alert extends HTMLElement {
  #legacyTitleElement?: HTMLParagraphElement;
  #legacyMessageElement?: TruncatedP;

  connectedCallback() {
    this.setAttribute('role', 'alert');
    this.classList.add(COMPONENT_NAME);

    if (!this.size && this.closest('.d-modal,.d-popup')) {
      this.size = 's';
    }

    const isLegacyAlert = this.matches('[type],[title],[message],[truncated-message]');
    if (isLegacyAlert) {
      const title = this.getAttribute('title');
      if (title) {
        this.title = title;
      }

      const typeAttr = this.getAttribute('type');
      if (typeAttr) {
        this.type = typeAttr as AlertType; // type assertion is fine, because the setter is safe
      }

      const message = this.getAttribute('message');
      if (message) {
        this.message = message;
      }

      const truncatedMessage = this.getAttribute('truncated-message');
      if (truncatedMessage) {
        this.truncatedMessage = truncatedMessage;
      }
    }

    this.#applyBg();
  }

  set type(value: AlertType) {
    ALERT_TYPES.forEach((type) => this.classList.toggle(`d-alert-${type}`, value === type));
    this.#applyBg();
  }

  get type(): AlertType {
    return ALERT_TYPES.find((type) => this.classList.contains(`d-alert-${type}`)) || 'error';
  }

  set size(size: '' | 's' | 'xs') {
    this.classList.toggle('d-alert-small', size === 's');
    this.classList.toggle('d-alert-xs', size === 'xs');
    this.#applyBg();
  }

  get size() {
    if (this.classList.contains('d-alert-xs')) {
      return 'xs';
    }
    if (this.classList.contains('d-alert-small')) {
      return 's';
    }
    return '';
  }

  #applyBg() {
    const hasBg = this.size !== 'xs';
    const hasDarkBg = hasBg && isOnBg(this, 'dark') && this.type !== 'warning';
    this.classList.toggle('dark-bg', hasDarkBg);
    this.classList.toggle('light-bg', hasBg && !hasDarkBg);
  }

  /** @deprecated Set the content of the alert via DOM APIs instead. */
  override set title(text: string) {
    this.#initLegacyElements();
    this.#legacyTitleElement!.innerHTML = text ? escapeAndMarkHTML(text) : '';
  }

  /** @deprecated Get the content of the alert via DOM APIs instead. */
  override get title() {
    return this.#legacyTitleElement?.innerHTML ?? '';
  }

  /** @deprecated Set the content of the alert via DOM APIs instead. */
  set message(text: string) {
    this.#initLegacyElements();
    this.#legacyMessageElement!.fullText = text;
  }

  /** @deprecated Get the content of the alert via DOM APIs instead. */
  get message() {
    return this.#legacyMessageElement?.fullText ?? '';
  }

  /** @deprecated Set the content of the alert via DOM APIs instead. */
  set truncatedMessage(text: string | undefined) {
    this.#initLegacyElements();
    this.#legacyMessageElement!.truncatedText = text;
  }

  /** @deprecated Get the content of the alert via DOM APIs instead. */
  get truncatedMessage() {
    return this.#legacyMessageElement?.truncatedText ?? '';
  }

  #initLegacyElements() {
    if (!this.#legacyTitleElement) {
      this.#legacyTitleElement = h('p.title');
      this.#legacyMessageElement = h('d-truncated-p.message');

      // Firefox + screen reader won't work here: https://github.com/nvaccess/nvda/issues/8873
      this.prepend(this.#legacyTitleElement, this.#legacyMessageElement);
    }
  }
}

customElements.define(COMPONENT_NAME, Alert);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: Alert;
  }
}
